import { useMemo } from 'react';
import { useUserWorkspaces } from '@/entities/user';
import type { WorkspaceRule } from '../../ui/WorkspaceWhitelistRulesTable';
import { type ModelProviderWhitelistResponseDto } from '@inference.engine/api-client';

/**
 * Groups workspace-level whitelisted providers by workspace
 */
export function useWorkspaceWhitelistRules(
  providers: ModelProviderWhitelistResponseDto[],
): WorkspaceRule[] {
  const workspaces = useUserWorkspaces();

  return useMemo(() => {
    const grouped = new Map<string, ModelProviderWhitelistResponseDto[]>();

    // Group workspace-level providers by workspace ID
    providers.forEach((provider) => {
      // Skip org-level whitelists (those without workspaceId)
      if (!provider.workspaceId) return;

      const workspaceId = provider.workspaceId;
      if (!grouped.has(workspaceId)) {
        grouped.set(workspaceId, []);
      }
      grouped.get(workspaceId)!.push(provider);
    });

    return Array.from(grouped.entries()).map(([workspaceId, workspaceProviders]) => ({
      workspaceId,
      workspaceName: workspaces.find((w) => w.id === workspaceId)?.name ?? workspaceId,
      providers: workspaceProviders,
    }));
  }, [providers, workspaces]);
}
